import React, { useEffect } from 'react';
import { User } from '../../../global';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import axiosHelper from '../../../helpers/axiosHelper';

import JobSelect from '../../Forms/Selects/JobSelect';
import SocialEditor from './SocialEditor';
import SkillsEditor from './SkillsEditor';
import { profilePreviewUrlForUser } from '../../../helpers/navigationUtils';
import FormField from '../../Forms/FormField';
import ErrorBlock from '../../Forms/ErrorBlock';
import { useBanner } from '../../../context/BannerContext';
interface Props {
  user: User;
  onChange: (user: User) => void;
}

const YourProfessionalProfile: React.FC<Props> = ({ user, onChange }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<Partial<User>>({
    defaultValues: user, // Pre-fill form with the user's data
  });

  const { createAndAddBannerMessage } = useBanner();
  
  useEffect(() => {
    console.log('resetting form');
    reset(user);
  }, [user, reset]);

  const onSubmit: SubmitHandler<Partial<User>> = async (data) => {
    console.log(data);
    const userdata = await axiosHelper.updateDSLUser(data);
    if (userdata) {
      createAndAddBannerMessage('Profile updated successfully!', 'success', 'profile-update-success');
      onChange(userdata);
    }
  };
  
  const gotoProfilePreview: SubmitHandler<Partial<User>> = (data) => {
    window.open(profilePreviewUrlForUser(user), 'blank');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="px-5 mt-7">
        <div className="row mb-7 mt-4">
          <div className="col-12 col-md-2 text fw-bold"></div>
          <div className="col-12 col-md-10">
            <span className="text color-blue">
              <i>
                <b>
                  <span className="form-required">*</span> &nbsp; Mandatory fields required to {user?.userType === 'D' ? 'upload your design' : 'launch a contest'}
                </b>
              </i>
            </span>
          </div>
        </div>

        {user.userType === 'D' && (
          <>
            <FormField name="job" label="Who are you" required noErrorBlock>
              <Controller
                name="job"
                control={control}
                rules={{ required: 'Job is required' }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <>
                    <JobSelect value={value} onChange={onChange} />
                    <ErrorBlock error={error} />
                  </>
                )}
              />
            </FormField>

            <Controller name="skills" control={control} render={({ field: { value, onChange } }) => <SkillsEditor skills={value || []} onChange={onChange} />} />
          </>
        )}
        {user?.userType === 'C' && (
          <>
            <FormField name="companyName" label="Company" error={errors.companyName}>
              <input type="text" {...register('companyName')} />
            </FormField>

            <FormField name="sector" label="Sector" error={errors.sector} required>
              <input type="text"
                {...register('sector', {
                  required: 'Sector is required',
                })}
              />
            </FormField>
          </>
        )}
        {user.userType === 'D' && (
          <>
            <FormField name="school1" label="School/ University" error={errors.school1}>
              <input
                type="text"
                {...register('school1', {
                  required: false,
                })}
              />
            </FormField>

            <FormField name="hasPublicFirstSchool">
                <input type="checkbox" className="" {...register('hasPublicFirstSchool')} tabIndex={0}/>
                <label>Public (School/ University visible in your profile page)</label>
            </FormField>

            <FormField name="school2" label="Other School/ University" error={errors.school2}>
              <input
                type="text"
                {...register('school2', {
                  required: false,
                })}
              />
            </FormField>

            <FormField name="hasPublicSecondSchool">
                <input type="checkbox" className="" {...register('hasPublicSecondSchool')} tabIndex={0}/>
                <label>Public (Other School/ University visible in your profile page)</label>
            </FormField>

            <FormField name="freelanceLicense" label="License to work as freelancer">
              <div className="col-12 d-flex flex-row justify-content-between align-items-center ">
                <div>
                  <input type="radio" value="Y" {...register('freelanceLicense')} tabIndex={0}/>
                  <label>Yes</label>
                </div>
                <div>
                  <input type="radio" value="F" {...register('freelanceLicense')} tabIndex={0}/>
                  <label>No</label>
                </div>
                <div></div>
                <div></div>
              </div>
            </FormField>

            <FormField name="companyName" label="Company" error={errors.companyName}>
              <input
                type="text"
                {...register('companyName', {
                  required: false,
                })}
              />
            </FormField>
          </>
        )}

        <FormField name="website" label="Website" error={errors.website}>
          <input
            type="text"
            {...register('website', {
              validate: (value) => !value || value.startsWith('https://') || 'URL must start with https://',
            })}
          />
        </FormField>

        <Controller name="socialNetworks" control={control} render={({ field: { value, onChange } }) => <SocialEditor socialNetworks={value || []} onChange={onChange} />} />

        <div className="row  d-flex justify-content-center align-items-center mb-7 mt-7">
          <div className={`btn-dark_blue col-md-2 mb-sm-3 mb-md-0 rounded mx-5 button`} onClick={handleSubmit(gotoProfilePreview)}>
            preview
          </div>

          <button className={`btn-success rounded col-md-2 mx-5 button`}>update</button>
        </div>
      </div>
    </form>
  );
};

export default YourProfessionalProfile;
